import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { noop, Subject } from 'rxjs';
import {
  AssignmentCustomerResponse,
  BASE_CUSTOMER_OFFER_STATUS_OPTIONS,
  baseAssignmentsSelectors,
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  BaseFilterService,
  baseInquiryActions,
  BaseInquiryOffersFilters,
  baseInquirySelectors,
  BaseInquiryService,
  BaseInquiryStatus,
  BaseModalService,
  BaseOfferStatus,
  BaseQueryParamService,
  BaseRolesService,
  InquiryOffersPagination,
  MetaPagination,
  OffersCountResponse,
  PspBranchFilterInfoResponse,
  QualificationShortInfoResponse,
  BASE_PERMISSIONS,
  ContractResponse,
  BaseCustomerInquiryOfferInterface,
  InquiryDateResponse,
  ShiftTimeResponse,
  ShiftTimeAndFlexResponse,
  BaseHelpersService,
  BaseInquiryShiftService,
  BaseShiftIcons,
  BaseInquiryStatusValue,
  BaseInquiryType,
} from 'base';
import { BaseCustomerInquiryExperienceOptionsConst } from '../../_const/base-customer-inquiry-offers-filters-options.const';
import { BaseCustomerInquiryService } from '../../inquiry.service';
import { BaseCustomerWorkCouncilStatusEnum } from '../../../../shared/_enums/base-customer-work-council-status.enum';
import { BaseCustomerLocationWorkCouncilSettingsInterface } from '../../../../shared/_interfaces/base-customer-location-work-council-settings.interface';
import { BASE_CUSTOMER_WORK_COUNCIL_APPROVAL_OPTIONS } from '../../../offers/_enums/base-customer-offers.enum';

@Component({
  selector: 'base-customer-inquiry-unlimited-offers',
  templateUrl: './base-customer-inquiry-unlimited-offers.component.html',
})
export class BaseCustomerInquiryUnlimitedOffersComponent implements OnInit, OnDestroy {
  @ViewChild('staffRequest', { static: true }) staffRequest: ElementRef;
  @ViewChild('closeInquiryModal', { static: true }) closeInquiryModal: ElementRef;
  @ViewChild('inquiredServiceProvidersModal') inquiredServiceProvidersModal: ElementRef;

  offers: BaseCustomerInquiryOfferInterface[] = [];
  filteredOffers: BaseCustomerInquiryOfferInterface[] = [];
  hasDirectlyInquiredOffers: boolean;
  inquiry: BaseInquiryType | undefined;
  shiftTimes: (ShiftTimeResponse & { index: number; label?: string })[] = [];
  shiftTimesWithFlex: (ShiftTimeAndFlexResponse & { index: number; label?: string })[] = [];
  offersCount: OffersCountResponse;
  isSidebarVisible = false;
  staffRequestModalRef: NgbModalRef;
  inquiredServiceProvidersModalRef: NgbModalRef;
  assignmentInfo: AssignmentCustomerResponse;
  canAcceptOffers: boolean;
  copiedInquiry: BaseInquiryType;
  canPublishInquiry: boolean;
  hasCloseInquiryPermission: boolean;
  inquiryListActiveFilters: { [key in any]: any } = {};
  pagination: MetaPagination;
  branches: PspBranchFilterInfoResponse[] = [];
  mainQualifications: QualificationShortInfoResponse[] = [];
  hasFiltersValues = false;
  baseOfferStatusOption = BASE_CUSTOMER_OFFER_STATUS_OPTIONS;
  baseCustomerWCApprovalOptions = BASE_CUSTOMER_WORK_COUNCIL_APPROVAL_OPTIONS;
  filtersForm = this.fb.group({
    [BaseInquiryOffersFilters.status]: undefined,
    [BaseInquiryOffersFilters.workCouncilStatus]: undefined,
    [BaseInquiryOffersFilters.rating]: 0,
    [BaseInquiryOffersFilters.experience]: undefined,
    [BaseInquiryOffersFilters.pspBranches]: [[] as number[]],
    [BaseInquiryOffersFilters.mainQualifications]: [[] as number[]],
    [BaseInquiryOffersFilters.search]: [''],
  });
  baseInquiryExperienceOption = BaseCustomerInquiryExperienceOptionsConst;
  validContract: ContractResponse;
  inquiryWeekdays: {
    mo: boolean;
    tu: boolean;
    we: boolean;
    th: boolean;
    fr: boolean;
    sa: boolean;
    su: boolean;
  };

  offersLoaded: boolean;
  profileName: string;
  isOfferPreselectionAllowed: boolean;
  locationWorkCouncilSettings: BaseCustomerLocationWorkCouncilSettingsInterface;

  baseInquiryStatus = BaseInquiryStatus;
  baseInquiryStatusValue = BaseInquiryStatusValue;
  offerStatuses = BaseOfferStatus;
  offerWorkCouncilStatuses = BaseCustomerWorkCouncilStatusEnum;
  avatarSize = BaseAvatarSize;
  buttonSizes = BaseButtonSize;
  baseShiftIconsEnum = BaseShiftIcons;
  onInquiryCloseModalRef: NgbModalRef;
  inquiryActions: string[] = [];

  private destroy$ = new Subject<boolean>();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private rolesService: BaseRolesService,
    public baseInquiryService: BaseInquiryService,
    private router: Router,
    private queryParamsService: BaseQueryParamService,
    public filterService: BaseFilterService,
    private fb: NonNullableFormBuilder,
    public baseCustomerInquiryService: BaseCustomerInquiryService,
    public activatedRoute: ActivatedRoute,
    private baseInquiryShiftService: BaseInquiryShiftService
  ) {}

  ngOnInit() {
    this.getInquiryActiveFilters();
    this.fetchFilteredInquiries();

    this.isOfferPreselectionAllowed =
      this.rolesService.hasAdminRole() ||
      this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PRESELECT_OFFERS);

    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);
    this.canAcceptOffers = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ACCEPT_OFFER);
    this.hasCloseInquiryPermission = this.rolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_CLOSE_INQUIRY
    );

    this.store
      .select(baseInquirySelectors.getCustomerInquiryOffers)
      .pipe(
        takeUntil(this.destroy$),
        filter((response) => response.ok !== undefined)
      )
      .subscribe((offers) => {
        this.mainQualifications = offers.filters?.mainQualifications || [];
        this.branches = offers.filters?.branches || [];
        this.offersLoaded = true;

        this.offers = offers.data;
        this.filteredOffers = this.offers;
        this.hasDirectlyInquiredOffers = this.filteredOffers.some((offer) => offer.data.profile.isDirectlyInquired);
        this.offersCount = {
          confirmed: this.offers.filter((offer) => offer.data.status === BaseOfferStatus.confirmed).length,
          needsConfirmation: this.offers.filter((offer) => offer.data.status === BaseOfferStatus.needsConfirmation)
            .length,
          declined: this.offers.filter((offer) => offer.data.status === BaseOfferStatus.declined).length,
          open: this.offers.filter((offer) => offer.data.status === BaseOfferStatus.open).length,
        };
        this.pagination = offers.pagination as MetaPagination;
      });

    this.store
      .select(baseInquirySelectors.getCustomerInquiry)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((response) => response.data as BaseInquiryType)
      )
      .subscribe((inquiry) => {
        this.inquiry = inquiry;
        this.shiftTimes = [];
        this.inquiryWeekdays = this.baseInquiryService.getInquiryWeekdays(this.inquiry.dates as InquiryDateResponse[]);
        this.copiedInquiry = { ...this.inquiry, id: inquiry.id };

        if (this.inquiry.shiftTimes?.length) {
          this.shiftTimes = this.baseInquiryShiftService.addIndexAndLabelToShiftTimes(this.inquiry.shiftTimes);
          this.shiftTimes = this.baseInquiryShiftService.sortShiftTimes(this.shiftTimes);
        }
        this.shiftTimesWithFlex = this.baseInquiryShiftService.addFlexShiftToShiftTimes(this.shiftTimes);
        this.locationWorkCouncilSettings = {
          workCouncilActivated: !!this.inquiry.customerLocation?.workCouncilActivated,
          acceptOfferAfterPreselection: !!this.inquiry.customerLocation?.acceptOfferAfterPreselection,
          acceptOfferAfterWorkCouncilApproval: !!this.inquiry.customerLocation?.acceptOfferAfterWorkCouncilApproval,
        };
        this.inquiryActions = this.baseCustomerInquiryService.setInquiryActions(
          this.inquiry,
          this.canPublishInquiry,
          this.hasCloseInquiryPermission
        );
      });

    this.store
      .select(baseAssignmentsSelectors.getAssignment)
      .pipe(takeUntil(this.destroy$))
      .subscribe((assignment) => {
        this.assignmentInfo = assignment?.data;
        this.validContract = this.assignmentInfo?.contracts?.find((contract) => contract.valid) as ContractResponse;
      });

    this.initFilterValuesByQueryParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();

    this.store.dispatch(baseInquiryActions.clearInquiryCustomerData());
  }

  onSearchChange(query: string): void {
    this.filtersForm.controls[BaseInquiryOffersFilters.search].setValue(query);
    this.queryParamsService.filterSubject.next({
      page: this.activatedRoute.snapshot.queryParams.page,
      filters: this.filtersForm.value,
    });
  }

  toggleSidebar(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  copyInquiry() {
    if (!this.inquiry) {
      return;
    }

    const copiedInquiry = { ...this.inquiry, id: -1 };
    const isMixedShifts = this.baseCustomerInquiryService.defineIsShiftTypesMixed(copiedInquiry);

    if (isMixedShifts) {
      this.baseCustomerInquiryService.openShiftViewPage(copiedInquiry, this.inquiry.id as number, true);
    } else {
      this.openNewStaffRequestModal();
    }
  }

  openNewStaffRequestModal(): void {
    this.staffRequestModalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.staffRequestModalRef.result.then(() => {
      this.isSidebarVisible = false;
    }, noop);
  }

  onPublish(): void {
    const modalRef = this.modalService.open(
      {
        headText: 'inquiry.publishInquiryNow',
        bodyText: 'inquiry.publishInquiryConfirmText',
        okText: 'action.yesPublish',
        cancelButtonBackground: 'secondary',
        cancelButtonDisplayed: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'md',
      }
    );

    modalRef.result.then(() => {
      this.updateCustomerInquiryStatus(BaseInquiryStatus.published);
    }, noop);
  }

  onClose(): void {
    this.onInquiryCloseModalRef = this.modalService.open(
      {
        headText: 'inquiry.closeInquiry.headText',
        bodyTemplate: this.closeInquiryModal,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'lg',
      }
    );

    this.onInquiryCloseModalRef.result.then((value: string) => {
      this.updateCustomerInquiryStatus(BaseInquiryStatus.closed, value);
    }, noop);
  }

  handleClick(actionType: string) {
    switch (actionType) {
      case 'action.edit':
        this.onEdit();
        break;
      case 'action.close':
        this.onClose();
        break;
      case 'action.publish':
        this.onPublish();
        break;
    }
  }

  getInquiryActiveFilters(): void {
    this.store
      .select(baseInquirySelectors.getCustomerSelectedFilters)
      .pipe(
        takeUntil(this.destroy$),
        map((state) => state.data)
      )
      .subscribe((data) => {
        if (data) {
          this.inquiryListActiveFilters = { ...data.filters };

          for (let i in this.inquiryListActiveFilters) {
            this.inquiryListActiveFilters[i] = this.inquiryListActiveFilters[i].toString();
          }
        }
      });
  }

  onScrollDown(): void {
    if (this.inquiry && this.pagination && this.pagination.page.current < this.pagination.page.total) {
      this.store.dispatch(
        baseInquiryActions.loadCustomerInquiryOffersList({
          payload: {
            ...this.activatedRoute.snapshot.queryParams,
            experience: BaseHelpersService.stringToBoolean(this.activatedRoute.snapshot.queryParams.experience),
            inquiry: this.inquiry.id as number,
            loadNextPage: true,
            page: this.pagination.page.current + InquiryOffersPagination.page,
          },
        })
      );
    }
  }

  openInquiredServiceProvidersModal(event: Event) {
    if (!this.inquiry) {
      return;
    }

    event.stopPropagation();
    this.store.dispatch(
      baseInquiryActions.loadInquiryProviders({
        payload: { inquiryId: this.inquiry.id as number },
      })
    );
    this.inquiredServiceProvidersModalRef = this.modalService.open(
      {
        headText: 'inquiry.inquiredServiceProvidersModal.headText',
        bodyTemplate: this.inquiredServiceProvidersModal,
        okButtonDisplayed: false,
        cancelButtonOnly: true,
        cancelText: 'action.close',
      },
      {
        backdrop: 'static',
        size: 'xl',
      }
    );
    this.inquiredServiceProvidersModalRef.result.then(noop, noop);
  }

  private initFilterValuesByQueryParams(): void {
    const filtersValues = this.filterService.setInitialFilterValuesByQueryParams(
      this.filtersForm.value,
      {
        [BaseInquiryOffersFilters.mainQualifications]: this.filterService.convertArrayToKeyValueObject(
          this.mainQualifications,
          'id',
          'name'
        ),
        [BaseInquiryOffersFilters.pspBranches]: this.filterService.convertArrayToKeyValueObject(
          this.branches,
          'id',
          'name'
        ),
        [BaseInquiryOffersFilters.rating]: this.queryParamsService.getQueryParam('rating'),
        [BaseInquiryOffersFilters.experience]: this.filterService.convertArrayToKeyValueObject(
          this.baseInquiryExperienceOption,
          'value',
          'label'
        ),
        [BaseInquiryOffersFilters.workCouncilStatus]: this.filterService.convertArrayToKeyValueObject(
          this.baseCustomerWCApprovalOptions,
          'value',
          'name'
        ),
      },
      ['rating', 'search']
    );
    this.hasFiltersValues = this.filterService.hasFilterValues(this.filtersForm.value);

    if (!this.hasFiltersValues) {
      return;
    }
    const statusValue = this.queryParamsService.getQueryParam('status');
    this.filtersForm.patchValue({
      ...filtersValues,
      experience: filtersValues.experience
        ? {
            value: JSON.parse(filtersValues.experience[0].value as string),
            label: filtersValues.experience[0].name,
          }
        : undefined,
      status: filtersValues.status
        ? this.baseOfferStatusOption.find((item) => item.id === parseInt(statusValue))
        : undefined,
    });
  }

  private fetchFilteredInquiries() {
    this.queryParamsService.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
      this.queryParamsService.redirectWithQueryParams(
        {
          ...value,
          filters: {
            ...value?.filters,
            experience: value?.filters?.experience?.value?.toString(),
            status: value?.filters?.status?.id?.toString(),
            workCouncilStatus: value?.filters?.workCouncilStatus?.value?.toString(),
          },
        },
        { search: 'value' },
        () => this.loadInquiryOffers()
      );
    });
  }

  private loadInquiryOffers(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    const payload = {
      ...queryParams,
      inquiry: this.copiedInquiry.id as number,
      profileAverageRating: queryParams.rating ? parseInt(queryParams.rating, 10) : undefined,
      experience: BaseHelpersService.stringToBoolean(queryParams.experience),
    };
    this.store.dispatch(
      baseInquiryActions.loadCustomerInquiryOffersList({
        payload: payload,
      })
    );
  }

  private updateCustomerInquiryStatus(status: 1 | 2 | 3, closedNote?: string): void {
    this.isSidebarVisible = false;
    this.store.dispatch(
      baseInquiryActions.updateInquiryStatus({
        payload: { inquiry: this.copiedInquiry.id as number, status, closedNote },
      })
    );
    this.router.navigate(['inquiries/list'], {
      queryParams: { status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` },
    });
  }

  private onEdit() {
    const inquiryId = this.copiedInquiry.id as number;

    this.baseCustomerInquiryService.getInquiry(inquiryId).subscribe((inquiry) => {
      this.baseCustomerInquiryService.openShiftViewPage(inquiry, inquiryId);
    });
  }
}
